import { Injectable } from '@angular/core';
import { IWorkoutTemplateDetail, User } from '../models';
import { HelperService } from './helper.service';
import { FitnessService } from './fitness.service';
import * as Sentry from "@sentry/angular-ivy";
import { DatabaseService } from './database.service';
import { StorageService } from './storage.service';
import { ThenableReference } from '@angular/fire/database';
import { IWorkoutProgramDetail, WorkoutProgram } from 'src/app/models/training/workout-program.model';
import { TrainerWorkout } from 'src/app/models/training/workout.model';
import { LocalAttachment } from './camera.service';
import { safeFind } from 'src/app/utils';

@Injectable({
  providedIn: 'root',
})
export class TrainingPlanService {

  constructor(
    private dbService: DatabaseService,
    private fitnessService: FitnessService,
    private helperService: HelperService,
    private storageService: StorageService,
  ) {}

  cleanSet(set) {
    set.exercises = set.exercises || [];
    const exercisesLength = set.exercises.length;
    const setTypes = this.fitnessService.getSetTypes();
    const lookupSet = setTypes[set.type] || safeFind(Object.values(setTypes), st => st.value === set.type);
    if(lookupSet && lookupSet.length && exercisesLength !== lookupSet.length) {
      const diff = Math.abs(lookupSet.length - exercisesLength);
      for (let index = 0; index < diff; index++) {
        if(exercisesLength < lookupSet.length) {
          // Add sets
          set.exercises.push({});
        } else {
          // Remove sets
          set.exercises.splice(set.exercises.length - 1, 1);
        }
      }
    }

    // Remove originalSets from training plans
    // This issue wwas caused by programs being saved from a tracked workout
    delete set.originalSets
  }

  async getWorkoutProgramAsPromise(userId: string, programId: string): Promise<WorkoutProgram> {
    const snap = await this.dbService.getSnapshot(`/workoutPrograms/users/${userId}/${programId}`)
    return snap? new WorkoutProgram({
      ...snap,
      id: programId
    }) : null;
  }

  async getWorkoutProgramsAsPromise(userId): Promise<WorkoutProgram[]> {
    const snap = await this.dbService.getSnapshot(`/workoutPrograms/users/${userId}`)
    let programs = this.helperService.transformFirebaseObjectToArray(snap);
    return programs.map((program) => {
      return new WorkoutProgram(program)
    });
  }

  async getPublishedWorkoutPrograms(userId: string) {
    let programs = await this.getWorkoutProgramsAsPromise(userId);
    return programs.filter((program: WorkoutProgram) => {
      return program.isPublished() || program.createdBy === userId;
    }).sort((a: WorkoutProgram, b: WorkoutProgram) => {
      return +new Date(b.startDate) - +new Date(a.startDate);
    });
  }

  async getActiveWorkoutPrograms(userId: string) {
    let programs = await this.getWorkoutProgramsAsPromise(userId);
    return programs.filter((program: WorkoutProgram) => {
      return program.isPublished() || program.createdBy === userId;
    }).sort((a: WorkoutProgram, b: WorkoutProgram) => {
      return +new Date(b.startDate) - +new Date(a.startDate);
    });
  }

  deleteWorkoutProgram(userId: string, programId: string): Promise<any> {
    return this.dbService.removeRef(`/workoutPrograms/users/${userId}/${programId}`);
  }

  getWorkoutProgramCommentsList(userId: string, programId: string) {
    return this.dbService.getListByRefTest(`/workoutPrograms/users/${userId}/${programId}/comments`);
  }
  
  addWorkoutComment(userId, workoutId, comment) {
    return this.dbService.pushByRef(`/workoutPrograms/users/${userId}/${workoutId}/comments`, comment)
  }

  async addWorkoutProgramCommentAttachment(
    userId,
    reportId,
    user: User,
    attachment: LocalAttachment,
    organization?: string
  ) {
    const storagePath = `users/${userId}/workoutPrograms/${reportId}/comments/${attachment.name}`;
    let attachmentRef = null;

    if(attachment.file) {
      attachmentRef = await this.storageService.fbPutFile(storagePath, attachment.file);
    }

    if(attachment.nativePath) {
      attachmentRef = await this.storageService.fpPutNativeFile(storagePath, attachment.nativePath);
    }

    if(!attachmentRef) {
      throw new Error('No attachment reference found');
    }

    return this.addWorkoutComment(userId, reportId, {
      comment: null,
      creationDate: new Date().toISOString(),
      createdBy: user.uid,
      createdByName: user.getFullName(),
      organization: organization || null,
      attachments: [{
        url: attachmentRef,
        fileName: attachment.name,
        type: attachment.type,
      }]
    });
  } 

  updateWorkoutProgramComment(userId: string, workoutId: string, commentId: string, comment) {
    return this.dbService.updateRef(`/workoutPrograms/users/${userId}/${workoutId}/comments/${commentId}`, comment);
  }

  deleteWorkoutComment(userId: string, workoutId: string, commentId: string) {
    return this.dbService.removeRef(`/workoutPrograms/users/${userId}/${workoutId}/comments/${commentId}`);
  }

  getUserWorkoutProgramList(userId: string) {
    return this.dbService.getListByRefTest<WorkoutProgram>(`workoutPrograms/users/${userId}`)
  }

  async saveUserWorkoutProgram(orgID: string, userId: string, programId: string, workoutProgramDetail: IWorkoutProgramDetail, modifiedBy: string): Promise<ThenableReference> {
    let result;
    let workoutProgram = {
      lastModifiedBy: modifiedBy,
      lastModifiedDate: new Date().toISOString(),
      organization: orgID ||  null,
      isPremium: !orgID,
      draftWorkoutProgram: workoutProgramDetail
    };
    if(programId === "add") {
      result = await this.dbService.pushByRef(`/workoutPrograms/users/${userId}`, {
        ...workoutProgram,
        createdBy: modifiedBy,
        creationDate: new Date().toISOString(),
        organization: orgID || null,
        readNotes:false
      });
    } else {
      result = await this.dbService.updateRef(`/workoutPrograms/users/${userId}/${programId}`, workoutProgram);
    }
    return result
  }

  async publishUserWorkoutProgram(orgID: string = null, userId: string, programId: string, workoutProgramDetail: IWorkoutProgramDetail, modifiedBy: string, notification = true): Promise<string> {
    let key;
    let workoutProgram: Partial<WorkoutProgram> = {
      ...workoutProgramDetail,
      lastModifiedBy: modifiedBy,
      lastModifiedDate: new Date().toISOString(),
      draftWorkoutProgram: null,
    };
    if(notification) {
      workoutProgram.published = new Date().toISOString()
    }
    if(!programId || programId === "add") {
      const res = await this.dbService.pushByRef(`/workoutPrograms/users/${userId}`, {
        ...workoutProgram,
        createdBy: modifiedBy,
        creationDate: new Date().toISOString(),
        organization: orgID || null,
        isPremium: !orgID
      });
      key = res.key;
    } else {
      await this.dbService.updateRef(`/workoutPrograms/users/${userId}/${programId}`, workoutProgram);
      key = programId;
    }
    return key
  }

  updateUserWorkoutProgram(userId: string, programId: string, data: Partial<IWorkoutProgramDetail>){
    return this.dbService.updateRef(`workoutPrograms/users/${userId}/${programId}`, data);
  } 


  updateUserWorkoutProgramDraft(userId: string, programId: string, data: Partial<IWorkoutProgramDetail>){
    return this.dbService.updateRef(`workoutPrograms/users/${userId}/${programId}/draftWorkoutProgram`, data);
  } 

  async saveOrganizationWorkoutProgram(orgID: string, programId: string, workoutProgramDetail: IWorkoutProgramDetail, modifiedBy: string): Promise<ThenableReference> {
    let result;
    let workoutProgram = {
      lastModifiedBy: modifiedBy,
      lastModifiedDate: new Date().toISOString(),
      organization: orgID,
      draftWorkoutProgram: workoutProgramDetail
    };
    if(programId === "add") {
      result = await this.dbService.pushByRef(`/workoutPrograms/organizations/${orgID}`, {
        ...workoutProgram,
        createdBy: modifiedBy,
        creationDate: new Date().toISOString(),
        organization: orgID,
      });
    } else {
      result = await this.dbService.updateRef(`/workoutPrograms/organizations/${orgID}/${programId}`, workoutProgram);
    }
    return result
  }

  async publishOrganizationWorkoutProgram(orgID: string, programId: string, workoutProgramDetail: IWorkoutProgramDetail, modifiedBy: string): Promise<string> {
    let key;
    let workoutProgram = {
      ...workoutProgramDetail,
      lastModifiedBy: modifiedBy,
      lastModifiedDate: new Date().toISOString(),
      draftWorkoutProgram: null,
      published: new Date().toISOString(),
    };
    if(programId === "add") {
      const res = await this.dbService.pushByRef(`/workoutPrograms/organizations/${orgID}`, {
        ...workoutProgram,
        createdBy: modifiedBy,
        creationDate: new Date().toISOString(),
        organization: orgID,
      });
      key = res.key;
    } else {
      await this.dbService.updateRef(`/workoutPrograms/organizations/${orgID}/${programId}`, workoutProgram);
      key = programId
    }
    return key
  }

  async publishOrganizationWorkoutTemplate(orgID: string, programId: string, workoutTemplateDetail: IWorkoutTemplateDetail, modifiedBy: string): Promise<string> {
    let key;
    let workoutTemplate = {
      ...workoutTemplateDetail,
      lastModifiedBy: modifiedBy,
      lastModifiedDate: new Date().toISOString(),
    };
    if(programId === "add") {
      const res = await this.dbService.pushByRef(`/workoutTemplates/organizations/${orgID}`, {
        createdBy: modifiedBy,
        creationDate: new Date().toISOString(),
        organization: orgID,
        ...workoutTemplate
      });
      key = res.key;
    } else {
      await this.dbService.updateRef(`/workoutTemplates/organizations/${orgID}/${programId}`, workoutTemplate);
      key = programId;
    }
    return key
  }

  async getOrganizationWorkoutTemplateAsPromise(organizationId: string, programId: string): Promise<IWorkoutTemplateDetail> {
    const snap = await this.dbService.getSnapshot(`workoutTemplates/organizations/${organizationId}/${programId}`);
    return snap? {id: programId, ...snap} : null;
  }

  // Training Programs
  getOrganizationWorkoutProgramsList(organizationId: string) {    
    return this.dbService.getListByRef<any>(`workoutPrograms/organizations/${organizationId}`, "id");
  }

  async getOrganizationWorkoutProgramAsPromise(organizationId: string, programId: string): Promise<WorkoutProgram> {
    const snap = await this.dbService.getSnapshot(`workoutPrograms/organizations/${organizationId}/${programId}`);
    return snap? new WorkoutProgram({id: programId, ...snap}) : null;
  }

  async getOrganizationWorkoutProgramsAsPromise(organizationId: string): Promise<WorkoutProgram[]> {
    const snap = await this.dbService.getSnapshot(`workoutPrograms/organizations/${organizationId}`);
    return snap? this.helperService.transformFirebaseObjectToArray(snap).map((program) => {return new WorkoutProgram(program) }) : null;
  }

  deleteOrganizationWorkoutProgram(organizationId: string, workoutProgramId: string): Promise<void> {
    return this.dbService.removeRef(`/workoutPrograms/organizations/${organizationId}/${workoutProgramId}`)
  }

  async uploadVideo(userId: string, exerciseId: string, workoutId: string, setIndex, exerciseIndex, attachment: LocalAttachment) {
    try {
      const storagePath = `users/${userId}/exercise/${exerciseId}/${attachment.name}`;
      let downloadURL: string;

      if(attachment.file) {
        downloadURL = await this.storageService.fbPutFile(storagePath, attachment.file);
      }

      if(attachment.nativePath) {
        downloadURL = await this.storageService.fpPutNativeFile(storagePath, attachment.nativePath);
      }

      if(!downloadURL) {
        throw new Error("Failed to upload video");
      }

      await this.updateTrackWorkout(userId, workoutId, setIndex, downloadURL, exerciseIndex);
      return downloadURL
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  }

  addTrackedExercise(userId, exerciseId, trackingData) {
    return this.dbService.pushByRef(`/trackedExercises/${userId}/${exerciseId}/`, trackingData);
  }

  updateTrackWorkout(userId, id, setIndex, url,exerciseIndex) { 
    return this.dbService.updateRef(`/workouts/tracked/${userId}/${id}/sets/${setIndex}/exercises/${exerciseIndex}`, { 'videoUrl': url })   
  }

  removeTrackWorkout(userId, id, setIndex, exerciseIndex) { 
    return this.dbService.removeRef(`/workouts/tracked/${userId}/${id}/sets/${setIndex}/exercises/${exerciseIndex}/videoUrl`);
  }

  getTrackedWorkoutCommentsList(userId: string, programId: string) {
    return this.dbService.getListByRefTest(`/workouts/tracked/${userId}/${programId}/comments`);
  }

  addTrackedWorkoutComment(userId, workoutId, comment) {
    return this.dbService.pushByRef(`/workouts/tracked/${userId}/${workoutId}/comments`, comment);
  }

  deleteTrackedWorkoutComment(userId, workoutId, commentId: string) {
    return this.dbService.removeRef(`/workouts/tracked/${userId}/${workoutId}/comments/${commentId}`);
  }

  async addTrackedWorkoutCommentAttachment(
    userId,
    reportId,
    user: User,
    attachment: LocalAttachment,
    organization?: string,
    uploadOptions?: { onProgress?: (progress: { percent: number }) => void }
  ) {
    const storagePath = `users/${userId}/workouts/tracked/${reportId}/comments/${attachment.name}`;
    let attachmentRef = null;

    if(attachment.file) {
      attachmentRef = await this.storageService.fbPutFile(storagePath, attachment.file, {
        resumable: true,
        onProgress: uploadOptions?.onProgress
          ? (p) => uploadOptions.onProgress({ percent: p.percent })
          : undefined,
      });
    }

    if(attachment.nativePath) {
      attachmentRef = await this.storageService.fpPutNativeFile(storagePath, attachment.nativePath);
    }

    if(!attachmentRef) {
      throw new Error('No attachment reference found');
    }

    return this.addTrackedWorkoutComment(userId, reportId, {
      comment: null,
      creationDate: new Date().toISOString(),
      createdBy: user.uid,
      createdByName: user.getFullName(),
      organization: organization || null,
      attachments: [{
        url: attachmentRef,
        fileName: attachment.name,
        type: attachment.type,
      }]
    });
  } 
  
  /* Organization Workouts */
  getOrganizationWorkoutsList(organizationId: string) {    
    return this.dbService.getListByRef<any>(`organizationWorkouts/${organizationId}`, "id");
  }

  async getOrganizationWorkoutAsPromise(organizationId: string, workoutId: string): Promise<TrainerWorkout> {
    const snap = await this.dbService.getSnapshot(`organizationWorkouts/${organizationId}/${workoutId}`);
    return snap? {id: workoutId, ...snap} : null;
  }

  async getOrganizationWorkoutsAsPromise(organizationId: string): Promise<TrainerWorkout[]> {
    const snap = await this.dbService.getSnapshot(`organizationWorkouts/${organizationId}`);
    return snap? this.helperService.transformFirebaseObjectToArray(snap) : null;
  }

  deleteOrganizationWorkout(organizationId: string, workoutId: string): Promise<void> {
    return this.dbService.removeRef(`/organizationWorkouts/${organizationId}/${workoutId}`);
  }

  async publishOrganizationWorkout(orgID: string, workoutId: string, workout: Partial<TrainerWorkout>, modifiedBy: string): Promise<string> {
    let result;
    let SC = {
      ...workout,
      lastModifiedBy: modifiedBy,
      lastModifiedDate: new Date().toISOString(),
    };
    if(!workoutId || workoutId === "add") {
      result = await this.dbService.pushByRef(`/organizationWorkouts/${orgID}`, {
        createdBy: modifiedBy,
        creationDate: new Date().toISOString(),
        organization: orgID,
        ...SC
      })
    } else {
      result = await this.dbService.updateRef(`/organizationWorkouts/${orgID}/${workoutId}`, SC)
    }
    return result? result.key : workoutId;
  }
}